import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Grid, CircularProgress, Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { api, gsecEntriesApi, trialBalanceApi } from '../../services/api';

const colors = {
  tableHeaderBackground: '#f5f5f5',
  sectionBackground: '#eef3fb',
  lightBackground: '#f9f9f9'
};

// Known chart-of-accounts code renames: the external ledger's historical
// entries stay tagged with whatever code was current when they were posted,
// so a renamed account shows its old and new codes as separate rows. Map old
// codes to their current canonical code before merging.
const ACCOUNT_CODE_ALIASES = {
  '111-101-170-044-44': '131-101-170-044-44'
};

const resolveAccountNet = (account) => {
  if (account?.net_balance !== undefined && account?.net_balance !== null && account?.net_balance !== '') {
    return Number(account.net_balance) || 0;
  }
  const opening =
    (Number(account?.opening_debit) || 0) - (Number(account?.opening_credit) || 0);
  const period =
    (Number(account?.total_debit) || 0) - (Number(account?.total_credit) || 0);
  return opening + period;
};

// The external API can return the same account_code as multiple rows with
// slightly different account_name spellings (it appears to snapshot the name
// on each ledger entry at posting time rather than joining the current chart
// of accounts, so a renamed account shows old-name and new-name rows split by
// when entries were posted). Merge by (aliased) account_code so the report
// shows one row per account with correctly summed totals.
const mergeByAccountCode = (accounts) => {
  const merged = new Map();
  for (const raw of accounts || []) {
    const code = ACCOUNT_CODE_ALIASES[raw.account_code] || raw.account_code;
    const account = code === raw.account_code ? raw : { ...raw, account_code: code };
    const existing = merged.get(code);
    if (!existing) {
      merged.set(code, { ...account });
    } else {
      existing.total_debit = (Number(existing.total_debit) || 0) + (Number(account.total_debit) || 0);
      existing.total_credit = (Number(existing.total_credit) || 0) + (Number(account.total_credit) || 0);
      existing.net_balance = (Number(existing.net_balance) || 0) + (Number(account.net_balance) || 0);
      if (existing.opening_debit !== undefined || account.opening_debit !== undefined) {
        existing.opening_debit = (Number(existing.opening_debit) || 0) + (Number(account.opening_debit) || 0);
      }
      if (existing.opening_credit !== undefined || account.opening_credit !== undefined) {
        existing.opening_credit = (Number(existing.opening_credit) || 0) + (Number(account.opening_credit) || 0);
      }
    }
  }
  return Array.from(merged.values());
};

/**
 * account_code -> { debit, credit, net } from a merged account list.
 * Used for both the main trial-balance ledger and the GSec-entries sheet so
 * we can rebuild the Combined Trial Balance (Equity/main TB + GSec ledger).
 */
const buildAccountFigureMap = (accounts) => {
  const map = {};
  for (const account of mergeByAccountCode(accounts || [])) {
    const debit = Number(account.total_debit) || 0;
    const credit = Number(account.total_credit) || 0;
    map[account.account_code] = {
      debit,
      credit,
      net: resolveAccountNet(account)
    };
  }
  return map;
};

const groupByCategory = (accounts) => {
  const groups = {};
  for (const account of mergeByAccountCode(accounts)) {
    const key = account.account_category || 'Uncategorized';
    if (!groups[key]) groups[key] = { sortKey: account.category_sort_key || key, accounts: [] };
    groups[key].accounts.push(account);
  }
  return Object.entries(groups)
    .sort((a, b) => String(a[1].sortKey).localeCompare(String(b[1].sortKey)))
    .map(([category, { accounts }]) => ({ category, accounts }));
};

const EXPORT_HEADERS = [
  'Category',
  'Account Code',
  'Account Name',
  'Debit',
  'Credit',
  'Net Balance',
  'As per Report',
  'Difference'
];

const csvEscape = (value) => {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const xmlEscape = (value) =>
  String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const GsecBalanceSheet = () => {
  const [asAtDate, setAsAtDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  // account_code -> figure computed from this system's own product reports
  // (GSec / T-Bill holdings as at the selected date), for reconciliation against
  // the combined trial-balance as-at net per account.
  const [reportFigures, setReportFigures] = useState({});
  // account_code -> { debit, credit, net } from Combined Trial Balance
  // (= main /trial-balance ledger + /gsec-entries balance-sheet ledger).
  const [combinedFigures, setCombinedFigures] = useState({});

  const generateReport = async () => {
    if (!asAtDate) {
      setError('Please select an As at date');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const asAtYmd = format(asAtDate, 'yyyy-MM-dd');
      // Match Combined Trial Balance "As at": no start date (Earliest → as at).
      const [result, tbResult, figuresResult] = await Promise.all([
        gsecEntriesApi.getBalanceSheet({
          endDate: asAtYmd
        }),
        trialBalanceApi
          .getTrialBalance({ endDate: asAtYmd })
          .catch((tbErr) => {
            console.error('Error fetching trial balance for GSec net balances:', tbErr);
            return null;
          }),
        api
          .get('/reports/account-figures', { params: { asAtDate: asAtYmd } })
          .then((r) => r.data)
          .catch((figErr) => {
            // Reconciliation figures are supplementary - don't fail the whole
            // report if this system's report computation errors out.
            console.error('Error fetching account report figures:', figErr);
            return null;
          })
      ]);
      if (result?.success) {
        const gsecMap = buildAccountFigureMap(result.data?.accounts);
        const tbMap = tbResult?.success
          ? buildAccountFigureMap(tbResult.data?.accounts)
          : {};
        const combined = {};
        const codes = new Set([...Object.keys(gsecMap), ...Object.keys(tbMap)]);
        for (const code of codes) {
          const gsec = gsecMap[code] || { debit: 0, credit: 0, net: 0 };
          const tb = tbMap[code] || { debit: 0, credit: 0, net: 0 };
          combined[code] = {
            debit: tb.debit + gsec.debit,
            credit: tb.credit + gsec.credit,
            net: tb.net + gsec.net
          };
        }
        setReport(result.data);
        setCombinedFigures(combined);
        setReportFigures(figuresResult?.success ? figuresResult.figures || {} : {});
      } else {
        setError(result?.message || 'Failed to generate GSec Balance Sheet.');
        setReport(null);
        setCombinedFigures({});
        setReportFigures({});
      }
    } catch (err) {
      console.error('Error generating GSec balance sheet:', err);
      setError(
        err.response?.data?.message ||
        'Failed to generate GSec Balance Sheet. Please try again later.'
      );
      setReport(null);
      setCombinedFigures({});
      setReportFigures({});
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const n = Number(amount) || 0;
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  };

  // net_balance = debit − credit → positive is DR, negative is CR (no minus sign).
  const formatNetBalance = (amount) => {
    const n = Number(amount) || 0;
    if (n === 0) return formatCurrency(0);
    const side = n > 0 ? 'DR' : 'CR';
    return `${formatCurrency(Math.abs(n))} ${side}`;
  };

  const resolveRowFigures = (account) => {
    const code = account?.account_code;
    if (code && combinedFigures[code]) {
      return combinedFigures[code];
    }
    // Fall back to the GSec sheet row if combined figures are unavailable.
    const debit = Number(account?.total_debit) || 0;
    const credit = Number(account?.total_credit) || 0;
    return {
      debit,
      credit,
      net: Number(account?.net_balance) || debit - credit
    };
  };

  const categoryGroups = groupByCategory(report?.accounts);
  const mergedSheetAccounts = mergeByAccountCode(report?.accounts || []);
  const grandTotalDebit = mergedSheetAccounts.reduce((sum, a) => sum + resolveRowFigures(a).debit, 0);
  const grandTotalCredit = mergedSheetAccounts.reduce((sum, a) => sum + resolveRowFigures(a).credit, 0);
  const grandTotalNet = mergedSheetAccounts.reduce((sum, a) => sum + resolveRowFigures(a).net, 0);

  const asAtLabel =
    report?.period?.endDate || (asAtDate ? format(asAtDate, 'yyyy-MM-dd') : '');

  const buildExportRows = () => {
    const rows = [];
    for (const { category, accounts } of categoryGroups) {
      let subDebit = 0;
      let subCredit = 0;
      let subNet = 0;
      for (const account of accounts) {
        const { debit, credit, net: netBalance } = resolveRowFigures(account);
        const reportCode =
          ACCOUNT_CODE_ALIASES[account.account_code] || account.account_code;
        const reportFigure =
          reportFigures[account.account_code] ?? reportFigures[reportCode];
        const hasFigure = reportFigure !== undefined && reportFigure !== null;
        const difference = hasFigure ? netBalance - Number(reportFigure) : null;
        subDebit += debit;
        subCredit += credit;
        subNet += netBalance;
        rows.push({
          category,
          account_code: account.account_code || '',
          account_name: account.account_name || '',
          debit,
          credit,
          net_balance: netBalance,
          net_balance_display: formatNetBalance(netBalance),
          as_per_report: hasFigure ? Number(reportFigure) : null,
          as_per_report_display: hasFigure ? formatNetBalance(reportFigure) : '',
          difference,
          row_type: 'account'
        });
      }
      rows.push({
        category,
        account_code: '',
        account_name: `Subtotal - ${category}`,
        debit: subDebit,
        credit: subCredit,
        net_balance: subNet,
        net_balance_display: formatNetBalance(subNet),
        as_per_report: null,
        as_per_report_display: '',
        difference: null,
        row_type: 'subtotal'
      });
    }
    rows.push({
      category: '',
      account_code: '',
      account_name: 'Grand Total',
      debit: grandTotalDebit,
      credit: grandTotalCredit,
      net_balance: grandTotalNet,
      net_balance_display: formatNetBalance(grandTotalNet),
      as_per_report: null,
      as_per_report_display: '',
      difference: null,
      row_type: 'grand_total'
    });
    return rows;
  };

  const handleExportCsv = () => {
    if (!report || categoryGroups.length === 0) return;
    const rows = buildExportRows();
    const lines = [
      csvEscape(`GSec Balance Sheet`),
      csvEscape(`As at ${asAtLabel}`),
      '',
      EXPORT_HEADERS.map(csvEscape).join(',')
    ];
    for (const row of rows) {
      lines.push(
        [
          row.category,
          row.account_code,
          row.account_name,
          row.debit,
          row.credit,
          row.net_balance_display,
          row.as_per_report_display,
          row.difference == null ? '' : row.difference
        ]
          .map(csvEscape)
          .join(',')
      );
    }
    const blob = new Blob([`\uFEFF${lines.join('\n')}`], {
      type: 'text/csv;charset=utf-8'
    });
    downloadBlob(blob, `gsec_balance_sheet_${asAtLabel || 'export'}.csv`);
  };

  const handleExportExcel = () => {
    if (!report || categoryGroups.length === 0) return;
    const rows = buildExportRows();
    const headerCells = EXPORT_HEADERS.map(
      (h) => `<th>${xmlEscape(h)}</th>`
    ).join('');
    const bodyRows = rows
      .map((row) => {
        const cells = [
          row.category,
          row.account_code,
          row.account_name,
          row.debit,
          row.credit,
          row.net_balance_display,
          row.as_per_report_display,
          row.difference == null ? '' : row.difference
        ]
          .map((c) => `<td>${xmlEscape(c)}</td>`)
          .join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8" />
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets>
<x:ExcelWorksheet><x:Name>GSec Balance Sheet</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
<h3>GSec Balance Sheet</h3>
<p>As at ${xmlEscape(asAtLabel)}</p>
<table border="1">
<thead><tr>${headerCells}</tr></thead>
<tbody>${bodyRows}</tbody>
</table>
</body></html>`;
    const blob = new Blob([`\uFEFF${html}`], {
      type: 'application/vnd.ms-excel;charset=utf-8'
    });
    downloadBlob(blob, `gsec_balance_sheet_${asAtLabel || 'export'}.xls`);
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        GSec Balance Sheet
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="As at"
                value={asAtDate}
                onChange={(newValue) => setAsAtDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={generateReport}
              disabled={loading || !asAtDate}
              fullWidth
            >
              Generate Report
            </Button>
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <Button
              variant="outlined"
              onClick={handleExportExcel}
              disabled={loading || !report || categoryGroups.length === 0}
              fullWidth
            >
              Excel
            </Button>
          </Grid>
          <Grid item xs={6} sm={2} md={2}>
            <Button
              variant="outlined"
              onClick={handleExportCsv}
              disabled={loading || !report || categoryGroups.length === 0}
              fullWidth
            >
              CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <Paper sx={{ p: 3 }} elevation={3}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              GSec Balance Sheet
            </Typography>
            <Typography variant="subtitle1">
              As at {asAtLabel}
            </Typography>
          </Box>

          {categoryGroups.length > 0 ? (
            categoryGroups.map(({ category, accounts }) => {
              const subtotalDebit = accounts.reduce((sum, a) => sum + resolveRowFigures(a).debit, 0);
              const subtotalCredit = accounts.reduce((sum, a) => sum + resolveRowFigures(a).credit, 0);
              const subtotalNet = accounts.reduce((sum, a) => sum + resolveRowFigures(a).net, 0);
              return (
                <TableContainer key={category} component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: colors.sectionBackground }}>
                        <TableCell colSpan={7} sx={{ fontWeight: 'bold' }}>{category}</TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: colors.tableHeaderBackground }}>
                        <TableCell>Account Code</TableCell>
                        <TableCell>Account Name</TableCell>
                        <TableCell align="right">Debit</TableCell>
                        <TableCell align="right">Credit</TableCell>
                        <TableCell align="right">Net Balance</TableCell>
                        <TableCell align="right">As per Report</TableCell>
                        <TableCell align="right">Difference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accounts.map((account) => {
                        const { debit, credit, net: netBalance } = resolveRowFigures(account);
                        const reportCode =
                          ACCOUNT_CODE_ALIASES[account.account_code] || account.account_code;
                        const reportFigure =
                          reportFigures[account.account_code] ?? reportFigures[reportCode];
                        const hasFigure = reportFigure !== undefined && reportFigure !== null;
                        const difference = hasFigure
                          ? netBalance - Number(reportFigure)
                          : null;
                        return (
                          <TableRow key={account.account_code} hover>
                            <TableCell>{account.account_code}</TableCell>
                            <TableCell>{account.account_name}</TableCell>
                            <TableCell align="right">
                              {debit ? formatCurrency(debit) : ''}
                            </TableCell>
                            <TableCell align="right">
                              {credit ? formatCurrency(credit) : ''}
                            </TableCell>
                            <TableCell align="right">{formatNetBalance(netBalance)}</TableCell>
                            <TableCell align="right">
                              {hasFigure ? formatNetBalance(reportFigure) : '—'}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={hasFigure && Math.abs(difference) > 0.01 ? { color: '#c62828', fontWeight: 'bold' } : undefined}
                            >
                              {hasFigure ? formatCurrency(difference) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ backgroundColor: colors.lightBackground }}>
                        <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Subtotal - {category}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(subtotalDebit)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(subtotalCredit)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNetBalance(subtotalNet)}</TableCell>
                        <TableCell align="right" />
                        <TableCell align="right" />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })
          ) : (
            <Typography align="center" sx={{ py: 4 }}>
              No account activity found as at this date
            </Typography>
          )}

          {categoryGroups.length > 0 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: colors.sectionBackground,
              borderRadius: 1
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Grand Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Debit {formatCurrency(grandTotalDebit)} &nbsp;|&nbsp; Credit {formatCurrency(grandTotalCredit)} &nbsp;|&nbsp; Net {formatNetBalance(grandTotalNet)}
              </Typography>
            </Box>
          )}
        </Paper>
      ) : null}
    </Box>
  );
};

export default GsecBalanceSheet;
